import React, { FC, useCallback, useEffect, useState } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isHeadingNode, $createHeadingNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import {
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
} from "lexical";

export const ToolbarPlugin: FC = () => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isH1, setIsH1] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      // Check if selection is collapsed (no text selected)
      setShowToolbar(!selection.isCollapsed());

      // Update format states
      setIsBold(selection.hasFormat("bold"));
      setIsUnderline(selection.hasFormat("underline"));

      // Check if current block is heading
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();

      setIsH1($isHeadingNode(element) && element.getTag() === "h1");
    } else {
      setShowToolbar(false);
    }
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        editor.getEditorState().read(() => {
          updateToolbar();
        });
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
  };

  const formatUnderline = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
  };

  const formatHeading = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (isH1) {
          // If already H1, convert back to paragraph
          $setBlocksType(selection, () => {
            return $createHeadingNode("h2"); // Temporarily use h2, then switch to paragraph
          });
        } else {
          // Convert to H1
          $setBlocksType(selection, () => {
            return $createHeadingNode("h1");
          });
        }
      }
    });
  };

  if (!showToolbar) return null;

  return (
    <div className="absolute z-10 bg-white border border-gray-300 rounded shadow-lg p-1 flex space-x-1 mt-2">
      <button
        type="button"
        onClick={formatBold}
        className={`px-3 py-1 rounded hover:bg-gray-100 font-semibold ${
          isBold ? "bg-primary-100 text-primary-600" : "text-gray-600"
        }`}
        aria-label="太字"
      >
        B
      </button>
      <button
        type="button"
        onClick={formatUnderline}
        className={`px-3 py-1 rounded hover:bg-gray-100 underline ${
          isUnderline ? "bg-primary-100 text-primary-600" : "text-gray-600"
        }`}
        aria-label="下線"
      >
        U
      </button>
      <button
        type="button"
        onClick={formatHeading}
        className={`px-3 py-1 rounded hover:bg-gray-100 font-bold text-lg ${
          isH1 ? "bg-primary-100 text-primary-600" : "text-gray-600"
        }`}
        aria-label="見出し1"
      >
        H1
      </button>
    </div>
  );
};
